"use client";
import { SignedIn, useAuth } from "@clerk/nextjs";
import Logo from "@workspace/ui/components/shared/logo";
import { InterviewMenu } from "./interview-menu";
import { InterviewGuestMenu } from "./interview-guest-menu";
import { useConnectionByCode } from "@casey/data-hooks";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "@workspace/ui/components/avatar";
import { Button } from "@workspace/ui/components/button";
import { Skeleton } from "@workspace/ui/components/skeleton";
import { X } from "lucide-react";
import { useParams } from "next/navigation";

interface InterviewHeaderProps {
  onPauseSession?: () => void;
  onHelp?: () => void;
  rightSlot?: React.ReactNode;
  guestUserId?: string | null;
  organizationName?: string | null;
  organizationAvatarUrl?: string | null;
  /** Connection code for fetching org branding (fallback when org props not provided) */
  connectionCode?: string | null;
  /** Loading state from parent (session metadata loading) */
  isLoading?: boolean;
  releaseWakeLock?: () => void;
  isSheetOpen?: boolean;
  onDismissSheet?: () => void;
}

export function InterviewHeader({
  onPauseSession,
  onHelp,
  rightSlot,
  guestUserId,
  organizationName: organizationNameProp,
  organizationAvatarUrl: organizationAvatarUrlProp,
  connectionCode: connectionCodeProp,
  isLoading: isLoadingProp,
  releaseWakeLock,
  isSheetOpen,
  onDismissSheet,
}: InterviewHeaderProps) {
  const params = useParams();
  const connectionCodeFromParams = params?.connectionCode as string | undefined;
  const { userId } = useAuth();

  // Get connection code from props or URL params (invite page)
  const connectionCode = connectionCodeProp || connectionCodeFromParams;

  // Fetch invite data if we're missing org branding props and have a connection code
  // This is used as a fallback for the invite page where session metadata isn't available,
  // or when session context is missing the avatar URL
  const shouldFetchInvite =
    connectionCode &&
    (!organizationNameProp || !organizationAvatarUrlProp) &&
    !isLoadingProp;

  const {
    organizationAvatarUrl: inviteAvatarUrl,
    organizationName: inviteOrgName,
    loading: inviteLoading,
  } = useConnectionByCode(connectionCode || "", {
    skip: !shouldFetchInvite,
  });

  // Use organization info from props first, then fallback to invite data
  const organizationIconUrl = organizationAvatarUrlProp || inviteAvatarUrl;
  const organizationName = organizationNameProp || inviteOrgName;

  // Generate initials from organization name
  const getInitials = (name: string | null) => {
    if (!name) return "O";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Show organization branding if we have organization info
  const showOrgBranding = organizationIconUrl || organizationName;

  // Show skeleton while loading (from parent or invite fetch)
  const isLoadingOrgBranding =
    isLoadingProp || (shouldFetchInvite && inviteLoading);

  return (
    <header className="border-b sticky top-0 bg-background z-50 h-16">
      <div className="container mx-auto px-4 h-full flex items-center relative">
        {isLoadingOrgBranding ? (
          <Skeleton className="h-8 w-8 rounded-md" />
        ) : showOrgBranding ? (
          <Avatar className="h-8 w-8 rounded-md">
            {organizationIconUrl && organizationIconUrl.trim() !== "" && (
              <AvatarImage
                src={organizationIconUrl}
                alt={organizationName || "Organization"}
              />
            )}
            <AvatarFallback className="rounded-md">
              {getInitials(organizationName)}
            </AvatarFallback>
          </Avatar>
        ) : (
          <Logo />
        )}
        {/* Middle section (flex grow) */}
        <div className="flex-grow"></div>
        <div className="flex items-center gap-4">
          {isSheetOpen && onDismissSheet ? (
            <Button variant="ghost" size="icon" onClick={onDismissSheet}>
              <X className="h-5 w-5" />
            </Button>
          ) : (
            <>
              {rightSlot}
              {/* Show menu for authenticated users */}
              <SignedIn>
                <InterviewMenu
                  onPauseSession={onPauseSession}
                  onHelp={onHelp}
                  releaseWakeLock={releaseWakeLock}
                />
              </SignedIn>
              {/* Show menu for guest users */}
              {!userId && guestUserId && (
                <InterviewGuestMenu
                  onPauseSession={onPauseSession}
                  onHelp={onHelp}
                  releaseWakeLock={releaseWakeLock}
                />
              )}
            </>
          )}
        </div>
      </div>
    </header>
  );
}
